const DEFAULT_MIME_TYPE = 'audio/mpeg';

export interface AudioConversionResult {
    file: File;
    url: string;
    duration: number;
    name: string;
}

/**
 * Converts base64 string to audio blob and returns file, URL, duration, and unique name
 * @param base64 - Base64 encoded audio string
 * @param mimeType - MIME type for the audio (defaults to 'audio/mpeg')
 * @returns Promise containing file, URL, duration, and unique name
 */
export const convertBase64ToAudioBlob = async (
    base64: string,
    mimeType: string = DEFAULT_MIME_TYPE,
): Promise<AudioConversionResult> => {
    return new Promise((resolve, reject) => {
        try {
            // Decode Base64 string into binary
            const byteCharacters = atob(base64);
            const uniqueId = Date.now().toString();

            const byteArrays: Uint8Array[] = [];

            for (let i = 0; i < byteCharacters.length; i += 1024) {
                const slice = byteCharacters.slice(i, i + 1024);
                const byteNumbers = new Array(slice.length)
                    .fill(0)
                    .map((_, j) => slice.charCodeAt(j));
                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }

            const blob = new Blob(byteArrays, { type: mimeType });
            const url = URL.createObjectURL(blob);

            const file = new File([blob], `${uniqueId}.mp3`, {
                type: mimeType,
            });

            // Calculate audio duration
            const audio = new Audio(url);
            audio.addEventListener('loadedmetadata', () => {
                const duration = Math.round(audio.duration * 100) / 100;
                resolve({
                    file,
                    url,
                    duration,
                    name: uniqueId,
                });
            });

            audio.addEventListener('error', (error) => {
                const errorMessage =
                    error && typeof error === 'object' && 'message' in error
                        ? error.message
                        : JSON.stringify(error);
                reject(
                    new Error(`Failed to load audio metadata: ${errorMessage}`),
                );
            });
        } catch (error) {
            reject(new Error(`Error decoding Base64 audio: ${error}`));
        }
    });
};
